import firebase from 'infras/firebase/client';
import { apiPath } from 'presentations/routes/path';
import { post } from 'infras/rest_client';
import { Provider, ProviderType } from 'domains/models/account/providerType';
import { UserItem } from 'presentations/api/schema/user';
import { currentTimeZone } from 'infras/datetime';
import { SessionCreateBody } from 'presentations/api/schema/account';

export enum TokenType {
  IdToken = 1,
  CustomToken = 2,
  Password = 3,
}

export interface StartSessionProps {
  to: string;
  tokenType: TokenType;
  token?: string;
  email?: string;
  password?: string;
  providerType: ProviderType;
  teamUid?: string;
}

export const signInWithGoogle = async (email: string | null = null) => {
  const provider = new firebase.auth.GoogleAuthProvider();
  try {
    // provider.addScope('https://www.googleapis.com/auth/calendar.readonly');
    // provider.addScope('https://www.googleapis.com/auth/calendar.events');
    // provider.addScope(
    //   'https://www.googleapis.com/auth/calendar.settings.readonly',
    // );
    provider.setCustomParameters({
      login_hint: email,
    });

    const result = await firebase.auth().signInWithPopup(provider);
    if (result.credential) {
      // Hg {a: null, idToken: "eyJhbGciOiJSUzI1NiIsImtpZCI6ImE0MWEzNTcwYjhlM2FlMW…4q4fR8aHfaQ1gHjDvh5CXVi2KXVhyvgwNx-zX6DPtKY2zOong", accessToken: "ya29.a0AfH6SMADJ05K5RpJw0U9x90WOXhjSRA2tnYFH1gLo-c…aEteysDB6COlFpSSn7xSsOukyB5XgiTK70KnlzSi-B_HSD3KQ", providerId: "google.com", signInMethod: "google.com"}
      firebase.auth().signInWithCredential(result.credential);
    }
    return result;
  } catch (error) {
    // https://firebase.google.com/docs/reference/js/firebase.auth.Auth?hl=ja#signinwithpopup
    console.debug(error);
  }
};

export const signInWithMicrosoft = async () => {
  const provider = new firebase.auth.OAuthProvider(Provider.Microsoft);
  try {
    // provider.addScope('Calendars.ReadWrite');
    provider.setCustomParameters({
      // tenant: process.env['MICROSOFT_TENANT_ID'],
    });

    const result = await firebase.auth().signInWithPopup(provider);
    console.error(result);
    return result;
  } catch (error) {
    // https://firebase.google.com/docs/reference/js/firebase.auth.Auth?hl=ja#signinwithpopup
    console.debug(error);
  }
};
export async function signInWithFacebook() {
  const provider = new firebase.auth.OAuthProvider(Provider.Facebook);
  try {
    const result = await firebase.auth().signInWithPopup(provider);
    return result;
  } catch (error) {
    console.debug(error);
  }
}

export const signInWithApple = async () => {
  const provider = new firebase.auth.OAuthProvider(Provider.Apple);
  provider.addScope('email');
  provider.addScope('name');
  // provider.setCustomParameters({
  //   locale: 'ja'
  // });

  try {
    const result = await firebase.auth().signInWithPopup(provider);
    return result;
  } catch (error) {
    // https://firebase.google.com/docs/reference/js/firebase.auth.Auth?hl=ja#signinwithpopup
    console.debug(error);
  }
};

export function signInWithSlack() {
  const clientId = process.env['SLACK_CLIENT_ID'];
  const state = encodeURIComponent(window.location.href);
  const userScope = [
    'identity.avatar',
    'identity.basic',
    'identity.email',
  ].join(' ');
  window.location.href = `https://slack.com/oauth/v2/authorize?client_id=${clientId}&state=${state}&user_scope=${userScope}`;
}

export function integrationWithSlack() {
  const clientId = process.env['SLACK_CLIENT_ID'];
  const state = encodeURIComponent(window.location.href);
  const redirectUri = encodeURIComponent(process.env['SLACK_REDIRECT_URL']!);
  const botScope = [
    'chat:write',
    'channels:read',
    'groups:read',
    // 'im:read',
    // 'mpim:read',
    // 'files:read',
  ].join(' ');
  const userScope = ['channels:read', 'groups:read'].join(' ');
  window.location.href = `https://slack.com/oauth/v2/authorize?client_id=${clientId}&redirect_uri=${redirectUri}&state=${state}&scope=${botScope}&user_scope=${userScope}`;
}

export function integrationWithSalesforce(clientId: string) {
  const state = `${process.env['JICOO_HOST']}/integrations`;
  const redirectUri = process.env['SALESFORCE_REDIRECT_URL'];
  const scope = ['refresh_token', 'api'].join(' ');

  window.location.href =
    `https://login.salesforce.com/services/oauth2/authorize?client_id=${clientId}` +
    `&response_type=code&redirect_uri=${redirectUri}&state=${state}` +
    `&scope=${encodeURIComponent(scope)}&display=popup`;
}

export function signInWithZoom() {
  const clientId = encodeURIComponent(
    String(process.env['ZOOM_OAUTH_CLIENT_ID']),
  );
  const state = encodeURIComponent(window.location.href);
  const redirectUri = encodeURIComponent(
    String(process.env['ZOOM_OAUTH_REDIRECT_URL']),
  );
  const url = `https://zoom.us/oauth/authorize?response_type=code&redirect_uri=${redirectUri}&state=${state}&client_id=${clientId}`;
  window.location.href = url;
}

export function teamsAuthUrl() {
  const state = encodeURIComponent(window.location.href);
  const tenant = process.env['MICROSOFT_TEAMS_TENANT_ID'];
  const clientId = process.env['MICROSOFT_TEAMS_OAUTH_CLIENT_ID'];
  const redirectUri = process.env['MICROSOFT_TEAMS_OAUTH_REDIRECT_URL']!;

  const scope = [
    'openid',
    'offline_access',
    'onlinemeetings.readwrite',
    'user.read',
  ].join(' ');
  const url =
    `https://login.microsoftonline.com/${tenant}/oauth2/v2.0/authorize` +
    `?client_id=${clientId}` +
    `&response_type=code&response_mode=query` +
    `&redirect_uri=${encodeURIComponent(redirectUri)}` +
    `&scope=${encodeURIComponent(scope)}` +
    `&state=${state}`;

  window.location.href = url;
}

export const createSession = async (result: any, locale?: string) => {
  const user = result.user as any;
  if (!user) {
    console.error('Invalid currentUser');
    return null;
  }

  const currentUser = firebase.auth().currentUser;
  if (!currentUser) {
    console.error('Invalid currentUser');
    return null;
  }

  const credential = result.credential as any;
  const idToken = await currentUser.getIdToken();

  let accessToken = null;
  if (credential) {
    accessToken = credential.accessToken;
  }

  let familyName = null;
  let givenName = null;

  const additionalUserInfo = result.additionalUserInfo as any;
  if (additionalUserInfo) {
    const profile = additionalUserInfo.profile;
    if (profile) {
      familyName = profile.family_name;
      givenName = profile.given_name;
    }
  }

  const data: SessionCreateBody = {
    uid: user.uid,
    idToken,
    accessToken,
    refreshToken: user.refreshToken,
    familyName,
    givenName,
    locale: locale || additionalUserInfo.locale,
    timeZone: currentTimeZone(),
  };
  const response = await post(apiPath('/v1/session/create'), { data });
  if (!response.isSuccess) {
    return null;
  }
  const viewer: UserItem = response.data;
  return viewer;
};

export const createSessionWithCustomToken = async (
  customToken: string,
  locale: string,
): Promise<UserItem | null> => {
  try {
    const result = await firebase.auth().signInWithCustomToken(customToken);
    return await createSession(result, locale);
  } catch (error) {
    console.debug(error);
  }
  return null;
};

export const createSessionWithEmailAndPassword = async (
  email: string,
  password: string,
  locale: string,
) => {
  try {
    const result = await firebase
      .auth()
      .signInWithEmailAndPassword(email, password);
    return await createSession(result, locale);
  } catch (error) {
    console.debug(error);
  }
  return null;
};

export const createSessionWithCredential = async (
  idToken: string,
  providerType: ProviderType | null = null,
  credential: firebase.auth.OAuthCredential | null = null,
  locale: string,
): Promise<UserItem | null> => {
  if (providerType === ProviderType.Google) {
    const credential = firebase.auth.GoogleAuthProvider.credential(idToken);
    let auth;
    try {
      auth = await firebase.auth().signInWithCredential(credential);
    } catch (error) {
      console.debug(error);
      return null;
    }
    if (!auth || !auth.user) {
      console.error('failed sign in with credential.');
      return null;
    }
    try {
      return await createSession(auth, locale);
    } catch (error) {
      console.log('failed create session.');
      return null;
    }
  } else if (providerType === ProviderType.Apple) {
    let auth;
    try {
      auth = await firebase.auth().signInWithCredential(credential!);
    } catch (error) {
      console.debug(error);
      return null;
    }
    if (!auth || !auth.user) {
      console.error('failed sign in with credential.');
      return null;
    }
    try {
      return await createSession(auth, locale);
    } catch (error) {
      console.log('failed create session.');
      return null;
    }
  }
  return null;
};

export const startTeamSession = async (teamUid: string) => {
  const result = await post(apiPath(`/v1/teams/${teamUid}/create_session`));
  if (!result.isSuccess) {
    return null;
  }
  return true;
};

export const getStartSessionUrl = (props: StartSessionProps) => {
  let redirectTo = props.to;
  if (redirectTo.startsWith('jicoo://login')) {
    const queries = [
      `tokenType=${props.tokenType}`,
      `providerType=${props.providerType}`,
      `teamUid=${props.teamUid}`,
    ];
    if (props.token) {
      queries.push(`token=${encodeURIComponent(props.token)}`);
    }
    if (props.email) {
      queries.push(`email=${encodeURIComponent(props.email)}`);
    }
    if (props.password) {
      queries.push(`password=${encodeURIComponent(props.password)}`);
    }
    redirectTo = `jicoo://login?${queries.join('&')}`;
  }
  return redirectTo;
};
